import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import { Modal } from "react-bootstrap";
import type { Ad } from "@models/Ad.model";
import type { AdStatus } from "@models/adGroup.model";
import { Brand } from "@models/brand.model";
import { EngagementService } from "@services/engagements/engagementService";
import { LaunchService } from "@services/launches/LaunchService";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import { NotificationService } from "@services/notifications/NotificationService";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { workflowEvents } from "src/modules/engagement/workflow/workflowEvents";
import Loader from "src/components/common/loader/loader";
import AdPicker from "@components/AdPicker/AdPicker";
import AdEditorModal from "src/modules/createEditor/AdEditorModal";
import RenameActionModal from "src/modules/contentCatalog/components/RenameActionModal";
import AdRow from "../adGroups/AdRow";
import "../adGroups/adGroups.scss";

/*
    Content is the whole of a CREATE engagement. It is the Reach Ad Groups view
    with the ad-group layer taken out: a CREATE engagement has no audiences to
    split its work across, so its ads hang off the campaign directly and the
    review track is the only structure that matters.

    No UTM toggle here, unlike the Reach view (MPN-1536). A CREATE engagement never
    publishes to an ad network, so there is no ad traffic for a utm_source tag to
    attribute. AdRow drops the control when onToggleUtm is absent, the same way the
    Content Catalog does.
*/

type ContentAd = {
  launch_ad_id: number;
  status: AdStatus;
  ad: Ad;
  source_template_name: string | null;
};

// organizationId is the campaign's sub-organization, which the ad editor's Approved URL
// picker scopes on -- same shape AdGroupsStep already reads. A client that publishes
// through sub-orgs keeps its approved URLs on the SUB-org, and the picker asks with
// `inherited` scope, which widens to ANCESTORS: asking as the parent returns neither the
// sub-org's URLs nor the one already saved on the ad, so a correct link rendered as an
// empty box over "Not on the approved list" (MPN-1954). Null when the campaign has no
// sub-org, which is every launch but Northbridge's four -- those fall back to the client.
type CampaignOption = { id: number; name: string; organizationId: number | null };

const RAIL_STAGES: { key: AdStatus | "ALL"; label: string }[] = [
  { key: "ALL", label: "All" },
  { key: "TODO", label: "To do" },
  { key: "IN_PROGRESS", label: "In progress" },
  { key: "IN_REVIEW", label: "In review" },
  { key: "CHANGES_REQUESTED", label: "Changes requested" },
  { key: "APPROVED", label: "Approved" },
  // See AdGroupDetail: hidden from "All", reachable through its own chip (MPN-1612).
  { key: "DO_NOT_USE", label: "Do not use" },
];

export default function ContentStep() {
  const { selectedEngagement } = useSnapshot(engagementStore);
  const engagementId = selectedEngagement?.id ?? 0;

  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [ads, setAds] = useState<ContentAd[]>([]);
  const [brand, setBrand] = useState<Brand>(new Brand());
  const [filter, setFilter] = useState<AdStatus | "ALL">("ALL");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssignAd, setShowAssignAd] = useState(false);
  const [showSubmitted, setShowSubmitted] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const adParam = searchParams.get("ad");
  /* Which campaign to open, when the caller knows -- a fix link off a failed publish
     names the launch that failed. Without it the first campaign wins, which on a
     multi-campaign engagement shows ads that had nothing to do with the failure. */
  const campaignParam = searchParams.get("campaign");
  const openAdId = adParam && /^\d+$/.test(adParam) ? Number(adParam) : null;

  /*
      A CREATE engagement is normally one campaign, but nothing enforces that,
      so the first is opened and the rest are offered as a switcher rather than
      being silently dropped.
  */
  useEffect(() => {
    if (!engagementId) return;
    let cancelled = false;
    new EngagementService()
      .getEngagementLaunches(engagementId)
      .then((loaded) => {
        if (cancelled) return;
        const options = loaded.map((c) => ({
          id: c.id,
          name: c.name,
          organizationId: c.organizations?.[0]?.id ?? null,
        }));
        setCampaigns(options);
        const requested = campaignParam ? Number(campaignParam) : null;
        // Only honour it if this engagement actually has that campaign; a stale link
        // should fall back rather than show an empty board.
        const preferred = options.some((o) => o.id === requested) ? requested : null;
        setCampaignId((current) => current ?? preferred ?? options[0]?.id ?? null);
        if (options.length === 0) setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load this engagement's campaign.");
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [engagementId, campaignParam]);

  const loadAds = useCallback(async () => {
    if (!campaignId) return;
    const launchAds = await new LaunchService().getLaunchAds({
      launch_id: campaignId,
      expand: "ad",
    });
    setAds(
      launchAds
        .filter((la) => !!la.ad)
        .map((la) => ({
          launch_ad_id: la.id,
          status: la.status as AdStatus,
          ad: la.ad!,
          source_template_name: la.source_template_name,
        })),
    );
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    loadAds()
      .catch(() => {
        if (!cancelled) setError("Could not load this engagement's content.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, loadAds]);

  useEffect(() => {
    const orgId = String(selectedEngagement?.client?.id ?? "");
    if (!orgId || orgId === "0") return;
    let cancelled = false;
    new OrganizationService()
      .getOrganizationBrand(orgId)
      .then((result) => {
        if (!cancelled && result) setBrand(result);
      })
      .catch(() => {
        // Brand fetch is non-critical — the editor falls back to defaults.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEngagement?.client?.id]);

  const counts = useMemo(() => {
    const byStatus: Record<AdStatus, number> = {
      TODO: 0,
      IN_PROGRESS: 0,
      IN_REVIEW: 0,
      CHANGES_REQUESTED: 0,
      APPROVED: 0,
      DO_NOT_USE: 0,
    };
    for (const a of ads) byStatus[a.status]++;
    return byStatus;
  }, [ads]);

  const visibleAds = ads.filter((a) => a.status !== "DO_NOT_USE");
  const filteredAds =
    filter === "ALL" ? visibleAds : ads.filter((a) => a.status === filter);
  const inProgressCount = counts.IN_PROGRESS;

  // The only place that writes ad status. Submit paths call this, then notify.
  // Returns whether the status actually changed. The submit paths below rely on it:
  // this catches its own error, so a caller that ignored the result carried on and
  // emailed the client about ads the server had just refused (MPN-2061).
  const setStatus = async (launchAdId: number, next: AdStatus): Promise<boolean> => {
    setBusyId(launchAdId);
    setError(null);
    const previous = ads;
    setAds((prev) => prev.map((a) => (a.launch_ad_id === launchAdId ? { ...a, status: next } : a)));
    try {
      await new LaunchService().updateLaunchAdStatus(launchAdId, next);
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
      return true;
    } catch (e: any) {
      setAds(previous);
      // The server's own words, not a generic retry prompt. A GuidePost refused for
      // having no destination URL (MPN-2061) will never succeed on a retry, and
      // "Try again" sent Ops round a loop with nothing telling them what to fix.
      setError(e?.message ?? "Could not change that ad's status. Try again.");
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const notifyClient = () =>
    new NotificationService().send_notification("CONTENT_REVIEW_REQUEST", {
      engagement: { id: engagementId },
    });

  const handleSubmitOne = async (launchAdId: number) => {
    if (!(await setStatus(launchAdId, "IN_REVIEW"))) return;
    await notifyClient();
    setShowSubmitted(true);
  };

  const handleSubmitAll = async () => {
    const batch = ads.filter((a) => a.status === "IN_PROGRESS");
    const refused: string[] = [];
    for (const ad of batch) {
      if (!(await setStatus(ad.launch_ad_id, "IN_REVIEW"))) refused.push(ad.ad.name);
    }
    // setStatus clears the banner on each pass, so only the last failure would have
    // survived the loop. Name them all, and do not tell the client to come and look
    // unless something actually went.
    if (refused.length) {
      setError(`Not submitted: ${refused.join(", ")}. See the ad for what is missing.`);
    }
    if (refused.length === batch.length) return;
    await notifyClient(); // once, after all of them
    setShowSubmitted(true);
  };

  const openEditor = (adId: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("ad", String(adId));
      return next;
    });
  };

  const closeEditor = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("ad");
        return next;
      },
      { replace: true },
    );
  };

  /*
      Assigning from the catalogue duplicates the template first, so editing the
      copy here can never write back into the catalogue entry it came from.
      Nothing links it to an audience: there are none at this level.
  */
  /*
      Renaming this campaign's copy of an ad (MPN-1508). The ad on this page is already a
      per-campaign duplicate, so the new name reaches the client's approval view and their
      catalog without touching the template or any other campaign using it. The link back to
      the template is a database reference and is unaffected.
  */
  const [retitling, setRetitling] = useState<Ad | null>(null);
  const [retitleBusy, setRetitleBusy] = useState(false);

  const handleRetitle = async (nextName: string) => {
    if (!retitling) return;
    setRetitleBusy(true);
    setError(null);
    try {
      await new ContentCatalogService().retitleAd(retitling.id, nextName);
      setRetitling(null);
      await loadAds();
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } catch {
      setError("Could not rename that ad. Try again.");
    } finally {
      setRetitleBusy(false);
    }
  };

  const handleAssign = async (selectedAdIds: number[]) => {
    if (!campaignId) return;
    setError(null);
    try {
      const catalogService = new ContentCatalogService();
      const duplicatedAdIds = await Promise.all(
        selectedAdIds.map(async (adId) => {
          const duplicated = await catalogService.duplicateAd(adId, { is_template: false });
          return duplicated.id as number;
        }),
      );
      await new LaunchService().addLaunchAds(campaignId, duplicatedAdIds);
      await loadAds();
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } catch {
      setError("Could not assign those ads. Try again.");
    }
  };

  if (isLoading) return <Loader />;

  if (campaigns.length === 0) {
    return (
      <div className="adgroup-section__empty">
        This engagement has no campaign yet, so there is nowhere to put content.
      </div>
    );
  }

  const currentCampaign = campaigns.find((c) => c.id === campaignId);

  return (
    <div className="adgroup-pane">
      <div className="adgroup-detail__head">
        <div className="adgroup-detail__title-row">
          <div>
            <h5 className="adgroup-detail__title">Content</h5>
            <div className="adgroup-detail__meta">
              {currentCampaign?.name ?? "No campaign"} · {ads.length}{" "}
              {ads.length === 1 ? "ad" : "ads"}
            </div>
          </div>
          <div className="adgroup-detail__head-actions">
            {inProgressCount > 1 && (
              <button type="button" className="btn btn-sm btn-primary" onClick={handleSubmitAll}>
                Submit all for review ({inProgressCount})
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowAssignAd(true)}
            >
              + Assign ad
            </button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Only worth showing when there is a choice to make. */}
      {campaigns.length > 1 && (
        <div className="stage-rail">
          {campaigns.map((campaign) => (
            <button
              key={campaign.id}
              type="button"
              className={`stage-rail__item${campaignId === campaign.id ? " on" : ""}`}
              onClick={() => setCampaignId(campaign.id)}
            >
              {campaign.name}
            </button>
          ))}
        </div>
      )}

      <div className="stage-rail">
        {RAIL_STAGES.map((stage) => (
          <button
            key={stage.key}
            type="button"
            className={`stage-rail__item${filter === stage.key ? " on" : ""}`}
            onClick={() => setFilter(stage.key)}
          >
            {stage.label}
            <span className="stage-rail__count">
              {stage.key === "ALL" ? visibleAds.length : counts[stage.key]}
            </span>
          </button>
        ))}
      </div>

      {filteredAds.length === 0 ? (
        <div className="adgroup-section__empty">
          {ads.length === 0 ? "No content yet — assign an ad to start." : "No ads in this stage"}
        </div>
      ) : (
        <div className="ad-grid">
          {filteredAds.map((a) => (
            <AdRow
              key={a.launch_ad_id}
              ad={a.ad}
              launchAdId={a.launch_ad_id}
              status={a.status}
              isBusy={busyId === a.launch_ad_id}
              onOpen={() => openEditor(a.ad.id)}
              onStatusChange={(next) => setStatus(a.launch_ad_id, next)}
              onSubmitForReview={() => handleSubmitOne(a.launch_ad_id)}
              onRetitle={() => setRetitling(a.ad)}
              sourceTemplateName={a.source_template_name}
            />
          ))}
        </div>
      )}

      <AdPicker
        isOpen={showAssignAd}
        onClose={() => setShowAssignAd(false)}
        serviceProviderOrganizationId={selectedEngagement?.service_provider?.id}
        clientOrganizationId={selectedEngagement?.client?.id}
        onSave={(ids) => void handleAssign(ids)}
      />

      <RenameActionModal
        show={retitling !== null}
        title="Rename for this campaign"
        initialValue={retitling?.name ?? ""}
        confirmLabel="Rename"
        isLoading={retitleBusy}
        onConfirm={(next) => void handleRetitle(next)}
        onClose={() => setRetitling(null)}
      />

      <AdEditorModal
        adId={openAdId ?? -1}
        brand={brand}
        modalOpen={openAdId != null}
        setModalOpen={(isOpen) => {
          if (!isOpen) closeEditor();
        }}
        launchId={campaignId ?? undefined}
        organizationId={currentCampaign?.organizationId ?? selectedEngagement?.client?.id}
      />

      <Modal show={showSubmitted} onHide={() => setShowSubmitted(false)} centered>
        <Modal.Body className="text-center py-4">
          <div className="fw-semibold mb-2">Request sent</div>
          <div className="small text-muted">Your request for review has been sent.</div>
          <button
            type="button"
            className="btn btn-sm btn-primary mt-3"
            onClick={() => setShowSubmitted(false)}
          >
            OK
          </button>
        </Modal.Body>
      </Modal>
    </div>
  );
}
